import React, { createContext, useContext, useEffect, useState } from "react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { loginRequest } from "../authConfig";
import axios from "axios";
import { setAdminAuthToken, setTokenRefresher } from "../adminApi";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:7071/api";

export interface UserProfile {
  id: number;
  entra_object_id: string;
  email: string;
  display_name: string;
  role: "ADMIN" | "APPROVER" | "READ_ONLY" | "POWER_USER" | "PO_REQUESTOR" | "PO_APPROVER" | "PO_SENIOR_APPROVER";
  approval_limit: number | null;
  allowed_vendor_numbers: number[];
  allowed_job_numbers: string[];
  notify_on_assignment: boolean;
  notify_on_escalation: boolean;
}

interface AuthContextType {
  user: UserProfile | null;
  loading: boolean;
  error: string | null;
  login: () => void;
  logout: () => void;
  isAdmin: boolean;
  isApprover: boolean;
  canUpload: boolean;
  isPowerUser: boolean;  // true for POWER_USER or ADMIN
  isPOUser: boolean;     // true for any PO role or higher (ADMIN, POWER_USER, PO_*)
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [user, setUser]       = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);

  useEffect(() => {
    if (inProgress !== InteractionStatus.None) return;
    if (isAuthenticated && accounts.length > 0) {
      initSession();
    } else {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, inProgress, accounts]);

  const initSession = async () => {
    try {
      setLoading(true);
      const account = instance.getActiveAccount() || accounts[0];
      if (!account) {
        setLoading(false);
        return;
      }
      const tokenResponse = await instance.acquireTokenSilent({
        ...loginRequest,
        account,
      });
      const response = await axios.post(
        `${API_BASE}/auth/session`,
        {},
        { headers: { Authorization: `Bearer ${tokenResponse.idToken}` } }
      );
      setUser(response.data);
      setAdminAuthToken(tokenResponse.idToken);
      // Register a token refresher so adminApi silently gets a fresh ID token
      // before every /mgmt/* request — prevents 401s after the 1-hour expiry.
      setTokenRefresher(async () => {
        const resp = await instance.acquireTokenSilent({ ...loginRequest, account });
        return resp.idToken;
      });
      setError(null);
    } catch (err: any) {
      const msg = err?.response?.data?.message || "Failed to initialize session";
      setError(msg);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = () => {
    instance.loginRedirect(loginRequest);
  };

  const logout = () => {
    instance.logoutRedirect();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      error,
      login,
      logout,
      isAdmin:     user?.role === "ADMIN",
      isApprover:  user?.role === "ADMIN" || user?.role === "APPROVER",
      canUpload:   user?.role === "ADMIN" || user?.role === "APPROVER",
      isPowerUser: user?.role === "ADMIN" || user?.role === "POWER_USER",
      isPOUser:    ["ADMIN", "POWER_USER", "PO_REQUESTOR", "PO_APPROVER", "PO_SENIOR_APPROVER"].includes(user?.role ?? ""),
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}